import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Breadcrums from "../../../common/breadcrumbs";
import { PER_PAGE_OPTIONS, STATUS, SWAL_SETTINGS, THEME_COLORS } from "../../../../../utils/Constants";
import {
    TrimText,
    formateDateWithMonth,
    handleServerValidations,
    hasP,
} from "../../../../../utils/commonfunction";
import * as subAdmin from "../../../services/subAdmin.service";
import { ExportUser, ExportUserPDF } from "../../../services/usermgmt.services";
import { useTranslation } from "react-i18next";
import { CustomProvider, DateRangePicker, Placeholder, Tooltip, Whisper } from "rsuite";
import { useQuery, useQueryClient } from "react-query";
import { AgGridReact } from "ag-grid-react";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import NoRowsOverlay from "../../../common/NoRowsOverlay";
import moment from "moment";

ModuleRegistry.registerModules([AllCommunityModule]);

const SubAdminList = () => {
    const params = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const gridRef = useRef();
    const queryClient = useQueryClient();
    const [searchData, setSearchData] = useState("");
    const [statsupdate, setStatusUpdate] = useState("false");
    const [pageIndex] = useState(Number(params.pgno) - 1 || 0);
    const [perPage, setPerPage] = useState(10);
    const [selectedRows, setSelectedRows] = useState([]);

    const breadcrumbs = [
        { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
        { title: t("Sub admin"), url: "" },
    ];

    const onSelectionChanged = (event) => {
        if (event && event.api) {
            const selectedNodes = event.api.getSelectedNodes();
            const selectedData = selectedNodes.map((node) => node.data);
            setSelectedRows(selectedData);
        }
    };

    const handleDeleteMultiple = () => {
        if (selectedRows.length === 0) return;
        Swal.fire({
            customClass: "swal-wide",
            title: t("msg_are_you_sure"),
            text: `Are you sure you want to delete ${selectedRows.length} selected record(s)? This action cannot be undone.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: THEME_COLORS.ADMIN_PRIMARY,
            cancelButtonColor: "#000",
            confirmButtonText: t("btn_yes_delete"),
            cancelButtonText: t("btn_cancel"),
        }).then((result) => {
            if (result.isConfirmed) {
                Swal.fire({
                    title: "Deleting...",
                    text: "Please wait while selected records are being deleted",
                    allowOutsideClick: false,
                    didOpen: () => {
                        Swal.showLoading();
                    },
                });

                const deletePromises = selectedRows.map((row) => subAdmin.Delete(row.originalId));
                Promise.all(deletePromises)
                    .then(() => {
                        setSelectedRows([]);
                        if (gridRef.current && gridRef.current.api) {
                            gridRef.current.api.deselectAll();
                        }
                        Swal.fire({
                            icon: "success",
                            title: "Deleted!",
                            text: `${selectedRows.length} record(s) deleted successfully.`,
                            ...SWAL_SETTINGS,
                        });
                        refetch();
                    })
                    .catch((error) => {
                        Swal.fire({
                            icon: "error",
                            text: handleServerValidations(error),
                            ...SWAL_SETTINGS,
                        });
                    });
            }
        });
    };

    // ================= FETCH ALL DATA =================
    const { data, isLoading, refetch } = useQuery({
        queryKey: ["subadmin-list"],
        queryFn: () => subAdmin.List({
            limit: 1000,
            lastKey: null,
            filters: {},
            sorting: {}
        }),
        refetchOnWindowFocus: false,
        retry: false,
        onError: (error) => {
            Swal.fire({
                icon: "error",
                text: handleServerValidations(error),
                ...SWAL_SETTINGS,
            });
        },
    });

    // ================= ACTIONS =================
    const viewfunction = (row) => {
        navigate(`/admin/user-management/sub-admins/view/${row.originalId}`);
    };

    const Editfunction = (row) => {
        navigate(`/admin/user-management/sub-admins/${params.pgno}/edit/${row.originalId}`);
    };

    const downloadPDF = async (row) => {
        try {
            const response = await ExportUserPDF(row.originalId);
            const blob = response instanceof Blob ? response : response?.data;
            if (blob instanceof Blob) {
                let fileName = "";
                const contentDisposition = response?.headers?.["content-disposition"];
                if (contentDisposition) {
                    const match = contentDisposition.match(/filename="?([^";]+)"?/);
                    if (match && match[1]) {
                        fileName = match[1];
                    }
                }
                if (!fileName) {
                    const surnameOrName = row.surname || row.lastName || row.username || row.firstName || row.name || row.originalId;
                    const safeSurname = String(surnameOrName).trim().replace(/[^a-zA-Z0-9_-]/g, "_");
                    fileName = `Sub_Admin_Report_${safeSurname}.pdf`;
                }

                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = fileName;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            } else {
                Swal.fire({ icon: "error", text: "Failed to download PDF. Please try again.", ...SWAL_SETTINGS });
            }
        } catch (err) {
            Swal.fire({ icon: "error", text: handleServerValidations(err), ...SWAL_SETTINGS });
        }
    };

    const ChangeStatus = (id, newStatus) => {
        const formData = new FormData();
        formData.append("status", newStatus);
        Swal.fire({
            customClass: "swal-wide",
            title: t("msg_are_you_sure"),
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: THEME_COLORS.ADMIN_PRIMARY,
            cancelButtonColor: "#000",
            confirmButtonText: t("btn_yes"),
            cancelButtonText: t("btn_cancel")
        }).then((result) => {
            if (result.isConfirmed) {
                if (gridRef.current?.api) {
                    const rowNode = gridRef.current.api.getRowNode(String(id));
                    if (rowNode) {
                        rowNode.setDataValue("status", newStatus);
                    }
                }

                subAdmin
                    .Status(formData, id)
                    .then((response) => {
                        if (response.success) {
                            Swal.fire({
                                icon: "success",
                                text: response.message,
                                ...SWAL_SETTINGS,
                            });
                        } else {
                            if (gridRef.current?.api) {
                                const rowNode = gridRef.current.api.getRowNode(String(id));
                                if (rowNode) {
                                    rowNode.setDataValue(
                                        "status",
                                        newStatus === STATUS.ACTIVE ? STATUS.INACTIVE : STATUS.ACTIVE
                                    );
                                }
                            }
                            Swal.fire({
                                icon: "error",
                                text: handleServerValidations(response),
                                ...SWAL_SETTINGS,
                            });
                        }
                    })
                    .catch((error) => {
                        if (gridRef.current?.api) {
                            const rowNode = gridRef.current.api.getRowNode(String(id));
                            if (rowNode) {
                                rowNode.setDataValue(
                                    "status",
                                    newStatus === STATUS.ACTIVE ? STATUS.INACTIVE : STATUS.ACTIVE
                                );
                            }
                        }
                        Swal.fire({
                            icon: "error",
                            text: handleServerValidations(error),
                            ...SWAL_SETTINGS,
                        });
                    });
            }
        });
    };

    // 🔥 OPTIMISTIC DELETE - NO REFRESH NEEDED
    const Deletefunction = (data) => {
        Swal.fire({
            customClass: "swal-wide",
            title: t("msg_are_you_sure"),
            text: t("you_not_be_able_to_revert_this"),
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: THEME_COLORS.ADMIN_PRIMARY,
            cancelButtonColor: "#000",
            confirmButtonText: t("btn_yes_delete"),
            cancelButtonText: t("btn_cancel")
        }).then((result) => {
            if (result.isConfirmed) {
                const rowId = data.originalId;

                // 🔥 OPTIMISTIC UPDATE - INSTANTLY REMOVE ROW
                if (gridRef.current?.api) {
                    gridRef.current.api.applyTransaction({
                        remove: [{ originalId: rowId }]
                    });
                }

                // API Call
                subAdmin.Delete(rowId).then((response) => {
                    if (response.success) {
                        Swal.fire({
                            icon: "success",
                            text: response.message,
                            ...SWAL_SETTINGS,
                            timer: 1500,
                            showConfirmButton: false
                        });
                    } else {
                        // 🔥 REVERT ON FAILURE - ADD ROW BACK
                        if (gridRef.current?.api && data) {
                            gridRef.current.api.applyTransaction({
                                add: [data]
                            });
                        }
                        Swal.fire({
                            icon: "error",
                            text: handleServerValidations(response),
                            ...SWAL_SETTINGS,
                        });
                    }
                }).catch((error) => {
                    // 🔥 REVERT ON ERROR - ADD ROW BACK
                    if (gridRef.current?.api && data) {
                        gridRef.current.api.applyTransaction({
                            add: [data]
                        });
                    }
                    Swal.fire({
                        icon: "error",
                        text: handleServerValidations(error),
                        ...SWAL_SETTINGS,
                    });
                });
            }
        });
    };

    const colDefs = useMemo(() => [
        {
            headerName: "",
            field: "checkbox",
            maxWidth: 50,
            checkboxSelection: true,
            headerCheckboxSelection: true,
            sortable: false,
            filter: false,
            suppressMovable: true,
            lockPosition: true,
        },
        {
            headerName: t("list_heading_sno"),
            field: "srNo",
            maxWidth: 85,
            sortable: false,
            filter: false,
            suppressMovable: true,
            lockPosition: true,
            valueGetter: (params) => {
                if (!params.node || params.node.rowIndex === undefined) return "";
                return params.node.rowIndex + 1;
            },
            cellStyle: { borderRight: "1px solid #dee2e6", color: "#3c4858" },
        },
        {
            headerName: t("label_name"),
            field: "name",
            flex: 1,
            minWidth: 200,
            sortable: true,
            filter: true,
        },
        {
            headerName: t("email"),
            field: "email",
            flex: 1,
            minWidth: 150,
            tooltipField: "email",
            cellRenderer: (params) => {
                return (
                    <div
                        style={{
                            width: "100%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {params.value || "N/A"}
                    </div>
                );
            },
        },
        {
            headerName: t("list_heading_created_date"),
            field: "createdAt",
            filter: "agDateColumnFilter",
            minWidth: 180,
            maxWidth: 150,
            sortable: true,
            valueGetter: (params) =>
                params.data?.createdAt ? new Date(params.data.createdAt) : null,
            valueFormatter: (params) =>
                params.value ? formateDateWithMonth(params.value) : "N/A"
        },
        ...(hasP("sub-admin-status") ? [{
            headerName: t("list_heading_status"),
            field: "status",
            filter: true,
            maxWidth: 90,
            sortable: true,
            cellStyle: {
                display: "flex",
                justifyContent: "center"
            },
            cellRenderer: (params) => (
                <label className={"custom-switch cp" + (params.value !== STATUS.ACTIVE ? " e_none" : "")}>
                    <input
                        type="checkbox"
                        className="custom-switch-input cp"
                        checked={params.value === STATUS.ACTIVE}
                        onChange={() =>
                            ChangeStatus(
                                params.data.originalId,
                                params.value === STATUS.ACTIVE ? STATUS.INACTIVE : STATUS.ACTIVE
                            )
                        }
                    />
                    <span className="custom-switch-indicator cp"></span>
                </label>
            ),
        }] : []),
        ...(hasP("sub-admin-view") || hasP("sub-admin-edit") || hasP("sub-admin-delete") ? [{
            headerName: t("list_heading_action"),
            maxWidth: 150,
            sortable: false,
            filter: false,
            cellRenderer: (params) => {
                const row = params.data;
                return (
                    <div className="btn-toolbar table-actions">
                        <div className="btn-group btn-group-sm">
                            {hasP("sub-admin-view") && (
                                <Whisper
                                    placement="top"
                                    trigger="hover"
                                    speaker={<Tooltip style={{ fontWeight: 'bold' }}>View</Tooltip>}
                                >
                                    <button
                                        type="button"
                                        className="btn view"
                                        onClick={() => viewfunction(row)}
                                    >
                                        <i className="ri-eye-fill"></i>
                                    </button>
                                </Whisper>
                            )}

                            {hasP("sub-admin-edit") && (
                                <Whisper
                                    placement="top"
                                    trigger="hover"
                                    speaker={<Tooltip style={{ fontWeight: 'bold' }}>Edit</Tooltip>}
                                >
                                    <button
                                        type="button"
                                        className="btn edit"
                                        onClick={() => Editfunction(row)}
                                    >
                                        <i className="ri-pencil-fill"></i>
                                    </button>
                                </Whisper>
                            )}

                            {hasP("sub-admin-delete") && (
                                <Whisper
                                    placement="top"
                                    trigger="hover"
                                    speaker={<Tooltip style={{ fontWeight: 'bold' }}>Delete</Tooltip>}
                                >
                                    <button
                                        type="button"
                                        className="btn delete"
                                        onClick={() => Deletefunction(row)}
                                    >
                                        <i className="ri-delete-bin-6-fill"></i>
                                    </button>
                                </Whisper>
                            )}
                            <Whisper
                                placement="top"
                                trigger="hover"
                                speaker={<Tooltip style={{ fontWeight: 'bold' }}>Export PDF</Tooltip>}
                            >
                                <button
                                    type="button"
                                    className="btn delete"
                                    style={{ color: '#e17055' }}
                                    onClick={() => downloadPDF(row)}
                                >
                                    <i className="ri-file-pdf-2-fill"></i>
                                </button>
                            </Whisper>
                        </div>
                    </div>
                );
            },
        }] : []),
    ], [t, navigate]);

    // 🔥 FIXED ROW DATA - S.No Dynamic
    const rowData = useMemo(() => {
        if (!data?.data?.list) return [];

        return data.data.list
            .map((row) => ({
                originalId: row.id,
                name: `${row?.firstName ? TrimText(row.firstName, 10) : "N/A"} ${row?.lastName ? TrimText(row.lastName, 10) : ""}`.trim(),
                email: row?.email || "N/A",
                status: row.status,
                createdAt: row.createdAt,
            }))
            .filter((row) =>
                searchData
                    ? row.name.toLowerCase().includes(searchData.toLowerCase()) ||
                    row.email.toLowerCase().includes(searchData.toLowerCase())
                    : true
            );
    }, [data?.data?.list, searchData]); // 🔥 Fixed dependency

    // ================= AG GRID CONFIG =================
    const getAgGridConfig = useMemo(() => ({
        ref: gridRef,
        domLayout: "autoHeight",
        rowData: rowData,
        columnDefs: colDefs,
        rowSelection: "multiple",
        onSelectionChanged: onSelectionChanged,
        getRowId: (params) => String(params.data.originalId),
        noRowsOverlayComponent: NoRowsOverlay,
        pagination: true,
        paginationPageSize: perPage,
        paginationPageSizeSelector: PER_PAGE_OPTIONS.map(opt => opt.value),
        suppressCellFocus: true,
        defaultColDef: {
            resizable: true,
            sortable: true,
            minWidth: 150,
            cellStyle: {
                padding: "10px 8px",
            },
            suppressHeaderMenuButton: true,
            suppressHeaderFilterButton: true,
        },
        headerHeight: 50,
        rowHeight: 45,
        onPaginationChanged: (params) => {
            if (params.api) {
                const currentPage = params.api.paginationGetCurrentPage();
                const pageSize = params.api.paginationGetPageSize();
                setPerPage(pageSize);
            }
        },
    }), [rowData, colDefs, perPage]);

    return (
        <>
            <Breadcrums data={breadcrumbs} />
            <div className="animation_fade">
                <div className="card custom-card overflow-hidden">
                    <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <div className="d-flex align-items-center gap-3">
                                <h6 className="main-content-label mb-0">Sub Admin</h6>
                                {selectedRows.length > 0 && (
                                    <button
                                        type="button"
                                        className="btn btn-danger btn-sm d-flex align-items-center gap-1"
                                        onClick={handleDeleteMultiple}
                                    >
                                        <i className="ri-delete-bin-line"></i> Delete Selected ({selectedRows.length})
                                    </button>
                                )}
                            </div>
                            <div className="d-flex align-items-center">
                                <div style={{ width: 300 }} className="me-3">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder={t("input_placeholder_search")}
                                        value={searchData}
                                        onChange={(e) => setSearchData(e.target.value)}
                                        style={{ outline: "none", boxShadow: "none" }}
                                    />
                                </div>

                                {/* status */}
                                <select
                                    className="form-select form-control me-3 cp"
                                    style={{ width: 100 }}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (gridRef.current && gridRef.current.api) {
                                            if (value && value.length) {
                                                gridRef.current.api.setFilterModel({
                                                    status: {
                                                        type: "equals",
                                                        filter: value,
                                                    }
                                                });
                                            } else {
                                                gridRef.current.api.setFilterModel(null);
                                            }
                                            gridRef.current.api.onFilterChanged();
                                        }
                                    }}
                                >
                                    <option value="">{t("list_heading_status")}</option>
                                    <option value={STATUS.ACTIVE}>{t("btn_active")}</option>
                                    <option value={STATUS.INACTIVE}>{t("btn_inactive")}</option>
                                </select>

                                {/* date */}
                                <div className="me-3">
                                    <CustomProvider>
                                        <DateRangePicker
                                            style={{ width: "150px" }}
                                            placeholder={t("input_placeholder_date")}
                                            placement="bottomEnd"
                                            onChange={(value) => {
                                                if (!gridRef?.current || !gridRef?.current?.api) return;

                                                if (!value || value?.length !== 2) {
                                                    gridRef?.current?.api?.setFilterModel(null)
                                                    gridRef?.current?.api?.onFilterChanged();
                                                    return
                                                }
                                                const [start, end] = value;
                                                gridRef?.current?.api?.setFilterModel({
                                                    createdAt: {
                                                        type: "inRange",
                                                        dateFrom: moment(start).startOf("day").format("YYYY-MM-DD HH:mm:ss"),
                                                        dateTo: moment(end).endOf("day").format("YYYY-MM-DD HH:mm:ss")
                                                    }
                                                })
                                            }}
                                        />
                                    </CustomProvider>
                                </div>

                                {hasP("sub-admin-add") && (
                                    <button
                                        className="btn ripple btn-main-primary signbtn ms-2"
                                        onClick={() =>
                                            navigate(`/admin/user-management/sub-admins/add`)
                                        }
                                    >
                                        {t("btn_add_new")}
                                    </button>
                                )}
                            </div>
                        </div>

                        {isLoading ? (
                            <Placeholder.Paragraph
                                className="w-100 admin-placeholder"
                                rowHeight={30}
                                rowSpacing={5}
                                active={true}
                                rows={7}
                            />
                        ) : (
                            <div className="ag-theme-alpine" style={{ width: '100%' }}>
                                <AgGridReact {...getAgGridConfig} />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default SubAdminList;
